import { Component, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  AuthService,
  OnlineProjectsService,
  ProjectDto,
  ProjectFoldersService,
  QuickstartProjectDto,
  QuickstartService,
  TeamsService,
} from '@entscheidungsnavi/api-client';
import { ProjectFolderDto, TeamProjectInfoWithRoleDto } from '@entscheidungsnavi/api-types';
import { OnDestroyObservable } from '@entscheidungsnavi/tools';
import { ConfirmModalComponent, Tag, YoutubeModalComponent } from '@entscheidungsnavi/widgets';
import { Observable, first, forkJoin, of, switchMap, take, takeUntil, zip } from 'rxjs';
import { ENVIRONMENT } from '../../../environments/environment';
import { JsonErrorModalComponent } from '../../../modules/shared/error/json-error-modal/json-error-modal.component';
import { supportedBrowsersRegexp } from '../../../supported-browsers';
import { ProjectService } from '../../data/project';
import { StartupService } from '../../data/startup.service';
import { YoutubeVideosService } from '../../data/youtube-videos.service';
import { ProjectListModalComponent } from '../../navigation';
import { ProjectListModalData, ProjectListModalResult } from '../../navigation/projects/project-list-modal/project-list-modal.component';
import { NewProjectModalComponent } from './new-project-modal.component';

@Component({
  templateUrl: './start.component.html',
  styleUrls: ['./start.component.scss'],
  standalone: false,
})
export class StartComponent {
  @OnDestroyObservable()
  private onDestroy$: Observable<void>;

  unsupportedBrowser: boolean;

  isOffline = false;
  quickstartProjects: readonly QuickstartProjectDto[];
  onlineAndTeamProjects: readonly (ProjectDto | TeamProjectInfoWithRoleDto)[];
  projectFolders: readonly ProjectFolderDto[];

  dragCounter = 0;
  showDragArea = false;

  @ViewChild('helpContent') helpContent: TemplateRef<any>;

  quickstartTags: Tag[];

  get isNightlyVersion() {
    return ENVIRONMENT.nightly;
  }

  constructor(
    private dialog: MatDialog,
    activatedRoute: ActivatedRoute,
    startupService: StartupService,
    private quickstartProjectsService: QuickstartService,
    private onlineProjectsService: OnlineProjectsService,
    private projectService: ProjectService,
    private authService: AuthService,
    private teamsService: TeamsService,
    private videosService: YoutubeVideosService,
    private projectFoldersService: ProjectFoldersService,
  ) {
    this.unsupportedBrowser = !supportedBrowsersRegexp.test(navigator.userAgent);

    activatedRoute.queryParams.pipe(first()).subscribe(params => {
      startupService.autorun(params);
    });

    zip(this.quickstartProjectsService.getProjects(), this.quickstartProjectsService.getTags()).subscribe({
      next: ([projects, tags]) => {
        this.quickstartProjects = projects;
        this.quickstartTags = tags;
      },
      error: () => (this.isOffline = true),
    });

    this.authService.loggedIn$
      .pipe(
        switchMap(loggedIn =>
          loggedIn
            ? zip(
                this.onlineProjectsService.getProjectList(),
                this.teamsService.getTeamProjects(),
                this.projectFoldersService.getProjectFolders(),
              )
            : of(null),
        ),
        takeUntil(this.onDestroy$),
      )
      .subscribe({
        next: data => {
          if (!data) {
            this.onlineAndTeamProjects = null;
            return;
          }
          this.onlineAndTeamProjects = [...data[0], ...data[1]];
          this.projectFolders = data[2];
        },
        error: () => (this.isOffline = true),
      });
  }

  newProjectClick() {
    this.dialog.open(NewProjectModalComponent);
  }

  openTemplatesModal(): void {
    if (this.isOffline || this.quickstartProjects == null || this.quickstartTags == null) {
      return;
    }

    this.dialog.open<ProjectListModalComponent, ProjectListModalData>(ProjectListModalComponent, {
      data: {
        quickstart: true,
        projectList: this.quickstartProjects,
        tags: this.quickstartTags,
        folderList: [],
      },
    });
  }

  openProjectList() {
    if (this.isOffline || this.onlineAndTeamProjects == null || this.projectFolders == null) return;

    this.dialog
      .open<ProjectListModalComponent, ProjectListModalData, ProjectListModalResult>(ProjectListModalComponent, {
        data: {
          quickstart: false,
          projectList: this.onlineAndTeamProjects,
          folderList: this.projectFolders,
          tags: [],
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.projectFolders = result.projectFolders;
          this.onlineAndTeamProjects = result.projects.filter(
            (p): p is ProjectDto | TeamProjectInfoWithRoleDto => !(p instanceof QuickstartProjectDto),
          );
        }
      });
  }

  dragEnter() {
    this.dragCounter++;
    this.showDragArea = true;
  }

  dragLeave() {
    this.dragCounter--;

    if (this.dragCounter === 0) {
      this.showDragArea = false;
    }
  }

  drop(event: DragEvent) {
    this.showDragArea = false;
    this.dragCounter = 0;

    event.preventDefault();

    if (event.dataTransfer.items.length !== 1) {
      this.dialog.open(ConfirmModalComponent, {
        data: {
          title: $localize`Fehler`,
          prompt: $localize`Es kann nur eine Projektdatei auf einmal geladen werden.`,
          buttonDeny: 'Okay',
          size: 'small',
        },
      });
      return;
    }

    const item = event.dataTransfer.items[0];
    if (item.kind !== 'file' || item.getAsFile().type !== 'application/json') {
      this.dialog.open(ConfirmModalComponent, {
        data: {
          title: $localize`Fehler`,
          prompt: $localize`Es können nur Projektdateien geladen werden.`,
          buttonDeny: 'Okay',
          size: 'small',
        },
      });
      return;
    }

    const file = item.getAsFile();
    this.loadProjectFile(file);
  }

  dragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  private loadProjectFile(file: File) {
    this.projectService.importFile(file).subscribe({
      error: () => {
        this.dialog.open(JsonErrorModalComponent);
      },
    });
  }

  public openVideoModal(): void {
    this.dialog.open(YoutubeModalComponent, {
      data: {
        videos: this.videosService.videos,
        categories: this.videosService.categories,
        categoryIdx: 0,
      },
    });
  }

  public updateProjects() {
    forkJoin([this.onlineProjectsService.getProjectList(), this.teamsService.getTeamProjects()])
      .pipe(take(1))
      .subscribe({
        next: ([onlineProjects, teamProjects]) => {
          // Combine the projects from both sources
          this.onlineAndTeamProjects = [...onlineProjects, ...teamProjects];
        },
        error: () => (this.isOffline = true),
      });
  }
}
